import { type Dispatch, type SetStateAction, useEffect, useState } from 'react'

import { FvLinkButton } from '@fv/client-components'
import type { FullShipment } from '@fv/client-types'

import { HasPermission } from '@/features/auth'
import { ShipperOnly } from '@/features/auth/HasRole'
import { WorkflowEnabled } from '@/features/auth/HasWorkflow'
import {
  type FilterType,
  MiniQuoteList,
} from '@/features/mini-quote-list/MiniQuoteList'
import { PrintRatesButton } from '@/features/quote/PrintRatesButton'
import { routes } from '@/routes'

import { DividedContent } from '../../DividedContent'
import { Pinline } from '../../Pinline'
import { TabLink } from '../../TabLink'

type Props = {
  isViewingActive: boolean
  load: FullShipment
  setShowRequestPanel: () => void
  setViewingActive: Dispatch<SetStateAction<boolean>>
}

const RatesPanel = ({
  isViewingActive,
  load,
  setShowRequestPanel,
  setViewingActive,
}: Props) => {
  const [filter, setFilter] = useState<FilterType>('active')
  const { selectedQuote } = load

  useEffect(() => {
    if (isViewingActive) {
      setFilter('active')
    }
  }, [isViewingActive])

  return (
    <div className="shipment-list-pricing">
      <DividedContent>
        <TabLink
          icon="comment-alt"
          transform="down-.5"
          isActive={isViewingActive}
          onClick={() => {
            setViewingActive(true)
          }}
        >
          Active
        </TabLink>
        <TabLink
          onClick={() => {
            setViewingActive(false)
            setFilter('pending')
          }}
          icon="comment-alt-minus"
          transform="down-.5"
          isActive={!isViewingActive}
        >
          Inactive
        </TabLink>
        <WorkflowEnabled workflow={load.workflow}>
          <ShipperOnly permissions={['load-creator']}>
            <FvLinkButton
              to={load.isMultiTruck ? routes.rates(load) : undefined}
              onClick={
                load.isMultiTruck
                  ? undefined
                  : () => {
                      setShowRequestPanel()
                    }
              }
              icon="paper-plane"
              transform="down-.5"
            >
              Get more quotes
            </FvLinkButton>
          </ShipperOnly>
          <HasPermission name="view-rates">
            <PrintRatesButton
              loadId={load.loadId}
              transform="down-.5"
              title="Print rates"
              className="pr-0"
            />
          </HasPermission>
        </WorkflowEnabled>
      </DividedContent>
      <Pinline rule />
      <DividedContent>
        {!isViewingActive && (
          <>
            <TabLink
              icon="comment-alt-minus"
              onClick={() => {
                setViewingActive(false)
                setFilter('pending')
              }}
              isActive={filter === 'pending'}
            >
              Have not responded
            </TabLink>
            <TabLink
              icon="comment-alt-slash"
              onClick={() => {
                setViewingActive(false)
                setFilter('declined')
              }}
              isActive={filter === 'declined'}
            >
              Declined
            </TabLink>
          </>
        )}
      </DividedContent>

      {!isViewingActive && <Pinline rule />}

      <MiniQuoteList filter={filter} key={selectedQuote?._id} load={load} />
    </div>
  )
}

export default RatesPanel
